import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  AlertCircle,
  BrainCircuit,
  Clock3,
  Coins,
  Database,
  Loader2,
  Search,
  Target,
  X,
  Zap
} from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import SkeletonBlock from '../../components/common/SkeletonBlock';
import { adminAPI } from '../../services/api';

const defaultFilters = {
  timeframeDays: 30,
  status: '',
  search: '',
  limit: 25,
  offset: 0
};

const array = (value) => (Array.isArray(value) ? value : []);
const number = (value) => Number(value) || 0;
const formatReference = (value) => value ? String(value).split('-')[0].toUpperCase() : 'N/A';

const formatNumber = (value, digits = 0) => new Intl.NumberFormat('en-US', {
  notation: Math.abs(number(value)) >= 10000 ? 'compact' : 'standard',
  maximumFractionDigits: digits
}).format(number(value));

const formatCandidateCount = (value) => {
  const count = number(value);
  return `${formatNumber(count)} ${count === 1 ? 'candidate' : 'candidates'}`;
};

const formatEvaluationCount = (value) => {
  const count = number(value);
  return `${formatNumber(count)} ${count === 1 ? 'evaluation' : 'evaluations'}`;
};

const formatRunCount = (value) => {
  const count = number(value);
  return `${formatNumber(count)} ${count === 1 ? 'run' : 'runs'}`;
};

const formatMatchCount = (value) => {
  const count = number(value);
  return `${formatNumber(count)} ${count === 1 ? 'match' : 'matches'}`;
};

const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: number(value) > 0 && number(value) < 0.01 ? 4 : 2,
  maximumFractionDigits: number(value) > 0 && number(value) < 0.01 ? 6 : 2
}).format(number(value));

const formatDateTime = (value) => {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not recorded';
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const formatDuration = (milliseconds) => {
  if (milliseconds === null || milliseconds === undefined) return 'In progress';
  const seconds = Math.max(0, Math.round(number(milliseconds) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

const formatHours = (hours) => {
  if (hours === null || hours === undefined) return 'No matches yet';
  if (number(hours) < 1) return `${Math.round(number(hours) * 60)}m`;
  if (number(hours) < 48) return `${formatNumber(hours, 1)}h`;
  return `${formatNumber(number(hours) / 24, 1)}d`;
};

const labelize = (value) => String(value || 'unknown')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase())
  .replace(/\bAi\b/g, 'AI');

const Chip = ({ children, tone = 'slate' }) => {
  const tones = {
    slate: 'border-slate-200 bg-slate-50 text-slate-700',
    emerald: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    rose: 'border-rose-200 bg-rose-50 text-rose-700',
    cyan: 'border-cyan-200 bg-cyan-50 text-cyan-700'
  };

  return <span className={`inline-flex rounded-md border px-2 py-1 text-xs font-medium ${tones[tone] || tones.slate}`}>{children}</span>;
};

const statusTone = (status) => {
  if (status === 'active' || status === 'succeeded') return 'emerald';
  if (status === 'failed') return 'rose';
  if (status === 'running') return 'cyan';
  if (status === 'skipped') return 'amber';
  return 'slate';
};

const MetricCard = ({ icon: Icon, label, value, detail }) => (
  <div className="bg-white rounded-[24px] p-6 shadow-sm border border-slate-100 flex flex-col justify-between hover:shadow-md transition-shadow">
    <div className="flex justify-between items-start mb-6">
      <h3 className="text-sm font-semibold text-slate-600">{label}</h3>
      <div className="w-10 h-10 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-600 border border-blue-100/50">
        <Icon className="w-5 h-5" />
      </div>
    </div>
    <div>
      <p className="text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
      {detail && <p className="text-xs font-medium text-slate-400 mt-2">{detail}</p>}
    </div>
  </div>
);

const Panel = ({ title, subtitle, children }) => (
  <section className="rounded-[24px] border border-slate-100 bg-white shadow-sm overflow-hidden flex flex-col">
    <header className="border-b border-slate-100 px-5 py-5 sm:px-6 bg-white">
      <h2 className="text-base font-semibold text-slate-900">{title}</h2>
      {subtitle && <p className="mt-1 text-sm leading-5 text-slate-500">{subtitle}</p>}
    </header>
    {children}
  </section>
);

const EmptyState = ({ children }) => (
  <div className="m-4 rounded-lg border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
    {children}
  </div>
);

const JobInsightsSkeleton = () => (
  <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
    <SkeletonBlock className="h-10 w-72 rounded-lg" />
    <SkeletonBlock className="mt-3 h-4 w-[42rem] max-w-full rounded-md" />
    <div className="mt-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }).map((_, index) => <SkeletonBlock key={index} className="h-32 rounded-[24px]" />)}
    </div>
    <SkeletonBlock className="mt-5 h-20 rounded-[24px]" />
    <SkeletonBlock className="mt-5 h-[440px] rounded-[24px]" />
  </div>
);

const StageFlow = ({ stages = {}, aggregate = false, runCount = 0 }) => {
  const stageUnit = aggregate ? 'evaluation' : 'candidate';
  const runContext = aggregate ? ` across ${formatRunCount(runCount)}` : '';
  const items = [
    {
      label: aggregate ? 'Stage 1 recall evaluations' : 'Stage 1 recall pool',
      value: stages.stage1,
      unit: stageUnit,
      detail: aggregate
        ? `${formatEvaluationCount(stages.stage1HardGate)} from hard gates + ${formatEvaluationCount(stages.stage1Ann)} from ANN recall${runContext}`
        : `${formatCandidateCount(stages.stage1HardGate)} from hard gates + ${formatCandidateCount(stages.stage1Ann)} from ANN recall`
    },
    {
      label: aggregate ? 'Stage 2 ranking evaluations' : 'Stage 2 ranked pool',
      value: stages.stage2,
      unit: stageUnit,
      detail: aggregate
        ? `Candidate scoring events by deterministic, vector, and lexical blend${runContext}`
        : 'Candidates scored by deterministic, vector, and lexical blend'
    },
    {
      label: aggregate ? 'Stage 3 review evaluations' : 'Stage 3 review set',
      value: stages.stage3,
      unit: stageUnit,
      detail: aggregate
        ? `${formatEvaluationCount(stages.stage3Reranked)} AI reranked${runContext}`
        : `${formatCandidateCount(stages.stage3Reranked)} AI reranked`
    },
    {
      label: 'Matches persisted',
      value: stages.matchesPersisted,
      unit: 'match',
      detail: 'Saved candidate-job matches after final persistence'
    }
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
      {items.map((item, index) => (
        <div key={item.label} className="relative rounded-2xl border border-slate-100 bg-slate-50 p-4 shadow-sm">
          <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500">0{index + 1}</p>
          <p className="mt-2 text-xl font-semibold text-slate-900">{formatNumber(item.value)}</p>
          <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">
            {number(item.value) === 1 ? item.unit : `${item.unit}s`}
          </p>
          <p className="mt-1 text-sm font-medium text-slate-700">{item.label}</p>
          <p className="mt-1 text-xs leading-5 text-slate-500">{item.detail}</p>
        </div>
      ))}
    </div>
  );
};

const DetailModal = ({ jobId, onClose }) => {
  const detailQuery = useQuery(
    ['admin-job-insight-detail', jobId],
    () => adminAPI.getJobInsightDetail(jobId),
    { enabled: Boolean(jobId), staleTime: 15000 }
  );

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [onClose]);

  const detail = detailQuery.data;
  const job = detail?.job || {};
  const latestRun = array(detail?.runs)[0];

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/50 p-0 backdrop-blur-sm sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Job AI insight detail">
      <button type="button" className="absolute inset-0 cursor-default" onClick={onClose} aria-label="Close detail" />
      <section className="relative flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-t-2xl bg-[#f8f9fa] shadow-2xl sm:rounded-2xl">
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-4 py-4 sm:px-6">
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Job AI insight · Ref {formatReference(job.id || jobId)}</p>
            <h2 className="mt-1 truncate text-xl font-semibold text-slate-900">{job.title || 'Loading job insight'}</h2>
            <p className="mt-1 text-sm text-slate-500">{job.employer?.companyName || 'Employer loading'}</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 hover:text-slate-900" aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div className="overflow-y-auto p-4 sm:p-6">
          {detailQuery.isLoading && (
            <div className="flex min-h-[320px] items-center justify-center text-slate-500">
              <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading job detail
            </div>
          )}

          {detailQuery.isError && (
            <div className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
              {detailQuery.error?.message || 'Job insight detail could not be loaded.'}
            </div>
          )}

          {detail && (
            <div className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <MetricCard icon={Coins} label="Estimated AI spend" value={formatCurrency(detail.usage?.estimatedCostUsd)} detail={`${formatNumber(detail.usage?.calls)} provider calls, ${formatNumber(detail.usage?.unpricedCalls)} unpriced`} />
                <MetricCard icon={Zap} label="AI tokens" value={formatNumber(detail.usage?.totalTokens)} detail={`${formatNumber(detail.usage?.promptTokens)} input (${formatNumber(detail.usage?.cachedPromptTokens)} cached), ${formatNumber(detail.usage?.completionTokens)} output`} />
                <MetricCard icon={Target} label="Matches" value={formatNumber(job.matches?.total)} detail={`First match in ${formatHours(job.matches?.hoursToFirstMatch)}`} />
                <MetricCard icon={Database} label="Matching runs" value={formatNumber(array(detail.runs).length)} detail={`${formatNumber(detail.usage?.failedCalls)} failed AI calls`} />
              </div>

              {!detail.tracking?.ready && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-800">
                  Matching-stage and token telemetry starts after the insights migration is deployed. Historical match timing remains available.
                </div>
              )}

              <Panel title="Latest matching funnel" subtitle={latestRun ? `${labelize(latestRun.triggerSource)} run on ${formatDateTime(latestRun.startedAt)}` : 'No tracked matching run exists for this job yet.'}>
                <div className="p-4 sm:p-5">
                  {latestRun ? <StageFlow stages={latestRun.stages} /> : <EmptyState>Run this job through matching after deployment to capture stage-level counts.</EmptyState>}
                </div>
              </Panel>

              <Panel title="Matching run history" subtitle="Each row represents one matching invocation with the trigger, stage counts, duration, tokens, and estimated spend captured at completion.">
                {array(detail.runs).length ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-[1050px] divide-y divide-slate-100 text-left text-sm">
                      <thead className="bg-slate-50/50 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                        <tr>
                          <th className="px-6 py-4">Run</th>
                          <th className="px-6 py-4">Trigger</th>
                          <th className="px-6 py-4">Stage 1 candidates</th>
                          <th className="px-6 py-4">Stage 2 candidates</th>
                          <th className="px-6 py-4">Stage 3 candidates</th>
                          <th className="px-6 py-4">Matches</th>
                          <th className="px-6 py-4">Tokens</th>
                          <th className="px-6 py-4">Spend</th>
                          <th className="px-6 py-4">Duration</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {array(detail.runs).map((run) => (
                          <tr key={run.id} className="hover:bg-slate-50">
                            <td className="px-6 py-4">
                              <p className="font-mono text-xs text-slate-700">#{run.id}</p>
                              <Chip tone={statusTone(run.status)}>{labelize(run.status)}</Chip>
                            </td>
                            <td className="px-6 py-4 text-slate-600">{labelize(run.triggerSource)}</td>
                            <td className="px-6 py-4 text-slate-600">{formatCandidateCount(run.stages?.stage1)}</td>
                            <td className="px-6 py-4 text-slate-600">{formatCandidateCount(run.stages?.stage2)}</td>
                            <td className="px-6 py-4 text-slate-600">{formatCandidateCount(run.stages?.stage3)}</td>
                            <td className="px-6 py-4 text-slate-600">{formatNumber(run.stages?.matchesPersisted)}</td>
                            <td className="px-6 py-4 text-slate-600">{formatNumber(run.aiUsage?.totalTokens)}</td>
                            <td className="px-6 py-4 text-slate-600">{formatCurrency(run.aiUsage?.estimatedCostUsd)}</td>
                            <td className="px-6 py-4 text-slate-600">{formatDuration(run.durationMs)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <EmptyState>No tracked matching runs have been recorded for this job.</EmptyState>}
              </Panel>

              <div className="grid gap-4 xl:grid-cols-2">
                <Panel title="AI usage by operation" subtitle="Provider usage associated with this job, including parsing, embeddings, and reranking.">
                  {array(detail.operations).length ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-[680px] divide-y divide-slate-100 text-left text-sm">
                        <thead className="bg-slate-50/50 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                          <tr>
                            <th className="px-6 py-4">Operation</th>
                            <th className="px-6 py-4">Model</th>
                            <th className="px-6 py-4">Calls</th>
                            <th className="px-6 py-4">Tokens</th>
                            <th className="px-6 py-4">Spend</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {array(detail.operations).map((operation) => (
                            <tr key={`${operation.operation}:${operation.provider}:${operation.model}`}>
                              <td className="px-6 py-4 font-medium text-slate-800">{labelize(operation.operation)}</td>
                              <td className="px-6 py-4 text-slate-600">{operation.model || operation.provider}</td>
                              <td className="px-6 py-4 text-slate-600">{formatNumber(operation.calls)}</td>
                              <td className="px-6 py-4 text-slate-600">{formatNumber(operation.totalTokens)}</td>
                              <td className="px-6 py-4 text-slate-600">{formatCurrency(operation.estimatedCostUsd)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <EmptyState>No AI usage has been attributed to this job yet.</EmptyState>}
                </Panel>

                <Panel title="Recent AI calls" subtitle="Most recent provider calls attributed to the job. Prompts and response content are never stored.">
                  {array(detail.recentCalls).length ? (
                    <div className="max-h-[360px] overflow-auto">
                      <table className="min-w-[620px] divide-y divide-slate-100 text-left text-sm">
                        <thead className="sticky top-0 bg-slate-50/50 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                          <tr>
                            <th className="px-6 py-4">Operation</th>
                            <th className="px-6 py-4">Status</th>
                            <th className="px-6 py-4">Tokens</th>
                            <th className="px-6 py-4">Latency</th>
                            <th className="px-6 py-4">Time</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {array(detail.recentCalls).map((call) => (
                            <tr key={call.id}>
                              <td className="px-6 py-4 font-medium text-slate-800">{labelize(call.operation)}</td>
                              <td className="px-6 py-4"><Chip tone={statusTone(call.status)}>{labelize(call.status)}</Chip></td>
                              <td className="px-6 py-4 text-slate-600">{formatNumber(call.totalTokens)}</td>
                              <td className="px-6 py-4 text-slate-600">{formatNumber(call.latencyMs)}ms</td>
                              <td className="px-6 py-4 text-slate-600">{formatDateTime(call.createdAt)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : <EmptyState>No provider calls have been attributed to this job.</EmptyState>}
                </Panel>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

function JobInsightsPage() {
  const [filters, setFilters] = useState(defaultFilters);
  const [searchInput, setSearchInput] = useState('');
  const [selectedJobId, setSelectedJobId] = useState(null);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setFilters((current) => ({ ...current, search: searchInput.trim(), offset: 0 }));
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  const queryKey = useMemo(() => ['admin-job-insights', filters], [filters]);
  const insightsQuery = useQuery(queryKey, () => adminAPI.getJobInsights(filters), {
    keepPreviousData: true,
    staleTime: 15000,
    refetchInterval: 30000
  });

  const data = insightsQuery.data;
  const summary = data?.summary || {};
  const stages = summary.stages || {};
  const usage = summary.aiUsage || {};
  const jobs = array(data?.jobs);
  const pagination = data?.pagination || {};

  if (insightsQuery.isLoading && !data) return <JobInsightsSkeleton />;

  return (
    <div className="min-h-full bg-[#f8f9fa] px-4 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-8">
      <PageHeader
        title="Job AI Insights"
        description="Inspect match timing, matching funnel volumes, AI token usage, and estimated provider spend for each job."
        actions={<Chip tone={data?.tracking?.ready ? 'emerald' : 'amber'}>{data?.tracking?.ready ? 'Tracking active' : 'Migration required'}</Chip>}
      />

      {insightsQuery.isError && !data && (
        <div className="rounded-lg border border-rose-200 bg-white p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
            <div>
              <h2 className="font-semibold text-slate-900">Job insights are unavailable</h2>
              <p className="mt-1 text-sm text-slate-500">{insightsQuery.error?.message || 'The request could not be completed.'}</p>
              <button type="button" onClick={() => insightsQuery.refetch()} className="mt-4 rounded-md bg-slate-950 px-4 py-2 text-sm font-medium text-white">Retry</button>
            </div>
          </div>
        </div>
      )}

      {data && (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <MetricCard icon={Coins} label="Estimated AI spend" value={formatCurrency(usage.estimatedCostUsd)} detail={`${formatNumber(usage.calls)} job-attributed calls, ${formatNumber(usage.unpricedCalls)} unpriced`} />
            <MetricCard icon={Zap} label="AI tokens" value={formatNumber(usage.totalTokens)} detail={`${formatNumber(usage.promptTokens)} input (${formatNumber(usage.cachedPromptTokens)} cached), ${formatNumber(usage.completionTokens)} output`} />
            <MetricCard icon={Target} label="Matched candidates" value={formatNumber(summary.totalMatches)} detail={`${formatNumber(summary.jobsWithMatches)} of ${formatNumber(summary.totalJobs)} jobs have saved matches`} />
            <MetricCard icon={Clock3} label="Matching runs" value={formatNumber(summary.runs)} detail={`Tracked in the last ${filters.timeframeDays} days; avg first match ${formatHours(summary.averageHoursToFirstMatch)}`} />
          </div>

          <div className={`mt-5 rounded-[24px] border p-4 text-sm leading-6 ${data.tracking?.ready ? 'border-cyan-200 bg-cyan-50 text-cyan-900' : 'border-amber-200 bg-amber-50 text-amber-900'}`}>
            <div className="flex items-start gap-3">
              <BrainCircuit className="mt-0.5 h-5 w-5 shrink-0" />
              <div>
                <p className="font-semibold">{data.tracking?.ready ? 'Forward telemetry is active' : 'Historical timing is available; telemetry migration is pending'}</p>
                <p>{data.tracking?.message}</p>
                {data.tracking?.trackingSince && <p className="mt-1 text-xs">Telemetry tracking starts at {formatDateTime(data.tracking.trackingSince)}. Spend uses the pricing snapshot stored with each provider call.</p>}
                {number(usage.unpricedCalls) > 0 && <p className="mt-1 text-xs font-semibold">{formatNumber(usage.unpricedCalls)} call(s) use a model without a configured rate and are excluded from estimated spend.</p>}
              </div>
            </div>
          </div>

          <div className="mt-5">
            <Panel title="Matching funnel" subtitle={`Stage totals are candidate evaluations across ${formatRunCount(summary.runs)} in the last ${filters.timeframeDays} days. Matched candidates are shown separately as saved matches.`}>
              <div className="p-4 sm:p-5">
                <StageFlow stages={stages} aggregate runCount={summary.runs} />
              </div>
            </Panel>
          </div>

          <div className="mt-5 rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
            <div className="grid gap-3 md:grid-cols-[minmax(220px,1fr)_160px_140px_110px]">
              <label className="relative">
                <span className="sr-only">Search jobs</span>
                <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-slate-400" />
                <input value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search job, employer, or exact job ID" className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 outline-none focus:border-slate-400 focus:ring-2 focus:ring-slate-100" />
              </label>
              <select value={filters.status} onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value, offset: 0 }))} className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-slate-400">
                <option value="">All job statuses</option>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="paused">Paused</option>
                <option value="closed">Closed</option>
                <option value="expired">Expired</option>
              </select>
              <select value={filters.timeframeDays} onChange={(event) => setFilters((current) => ({ ...current, timeframeDays: Number(event.target.value), offset: 0 }))} className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-slate-400">
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last year</option>
              </select>
              <select value={filters.limit} onChange={(event) => setFilters((current) => ({ ...current, limit: Number(event.target.value), offset: 0 }))} className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-700 outline-none focus:border-slate-400">
                <option value={10}>10 rows</option>
                <option value={25}>25 rows</option>
                <option value={50}>50 rows</option>
                <option value={100}>100 rows</option>
              </select>
            </div>
          </div>

          <div className="mt-5">
            <Panel title="Jobs" subtitle="Historical match timing is lifetime data. Funnel, token, and spend columns reflect the selected telemetry window.">
            {jobs.length ? (
              <>
                <div className="hidden overflow-x-auto md:block">
                  <table className="min-w-[1240px] divide-y divide-slate-100 text-left text-sm">
                    <thead className="bg-slate-50/50 text-[10px] font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">
                      <tr>
                        <th className="px-6 py-4">Job</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4">First match</th>
                        <th className="px-6 py-4">Matched candidates</th>
                        <th className="px-6 py-4">Stage 1 evals</th>
                        <th className="px-6 py-4">Stage 2 evals</th>
                        <th className="px-6 py-4">Stage 3 evals</th>
                        <th className="px-6 py-4">Tokens</th>
                        <th className="px-6 py-4">Spend</th>
                        <th className="px-6 py-4">Runs</th>
                        <th className="px-6 py-4" />
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100 bg-white">
                      {jobs.map((job) => (
                        <tr key={job.id} className="hover:bg-slate-50">
                          <td className="px-6 py-4">
                            <p className="max-w-[260px] truncate font-medium text-slate-900">{job.title}</p>
                            <p className="mt-1 max-w-[260px] truncate text-xs text-slate-500">Ref {formatReference(job.id)} · {job.employer?.companyName}</p>
                          </td>
                          <td className="px-6 py-4"><Chip tone={statusTone(job.status)}>{labelize(job.status)}</Chip></td>
                          <td className="px-6 py-4 text-slate-600">{formatHours(job.matches?.hoursToFirstMatch)}</td>
                          <td className="px-6 py-4 text-slate-600">{formatMatchCount(job.matches?.total)}</td>
                          <td className="px-6 py-4 text-slate-600">{formatEvaluationCount(job.tracking?.stages?.stage1)}</td>
                          <td className="px-6 py-4 text-slate-600">{formatEvaluationCount(job.tracking?.stages?.stage2)}</td>
                          <td className="px-6 py-4 text-slate-600">{formatEvaluationCount(job.tracking?.stages?.stage3)}</td>
                          <td className="px-6 py-4 text-slate-600">{formatNumber(job.aiUsage?.totalTokens)}</td>
                          <td className="px-6 py-4 text-slate-600">{formatCurrency(job.aiUsage?.estimatedCostUsd)}</td>
                          <td className="px-6 py-4 text-slate-600">{formatRunCount(job.tracking?.runs)}</td>
                          <td className="px-6 py-4 text-right">
                            <button type="button" onClick={() => setSelectedJobId(job.id)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm border border-slate-100/50"><Search className="w-4 h-4" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="divide-y divide-slate-100 md:hidden">
                  {jobs.map((job) => (
                    <article key={job.id} className="space-y-3 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="truncate font-semibold text-slate-900">{job.title}</p>
                          <p className="mt-1 truncate text-xs text-slate-500">Ref {formatReference(job.id)} · {job.employer?.companyName}</p>
                        </div>
                        <Chip tone={statusTone(job.status)}>{labelize(job.status)}</Chip>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-500">First match</p><p className="mt-1 font-medium text-slate-800">{formatHours(job.matches?.hoursToFirstMatch)}</p></div>
                        <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-500">Matched candidates</p><p className="mt-1 font-medium text-slate-800">{formatMatchCount(job.matches?.total)}</p></div>
                        <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-500">Tokens</p><p className="mt-1 font-medium text-slate-800">{formatNumber(job.aiUsage?.totalTokens)}</p></div>
                        <div className="rounded-lg bg-slate-50 p-2"><p className="text-xs text-slate-500">Runs</p><p className="mt-1 font-medium text-slate-800">{formatRunCount(job.tracking?.runs)}</p></div>
                      </div>
                      <div className="flex justify-end pt-2">
                        <button type="button" onClick={() => setSelectedJobId(job.id)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shadow-sm border border-slate-100/50" title="Inspect">
                          <Target className="w-4 h-4" />
                        </button>
                      </div>
                    </article>
                  ))}
                </div>
              </>
            ) : <EmptyState>No jobs match the current filters.</EmptyState>}

            <footer className="flex flex-col gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-500 sm:flex-row sm:items-center sm:justify-between">
              <p>Showing {jobs.length ? number(filters.offset) + 1 : 0}-{number(filters.offset) + jobs.length} of {formatNumber(pagination.total)}</p>
              <div className="flex gap-2">
                <button type="button" disabled={!filters.offset} onClick={() => setFilters((current) => ({ ...current, offset: Math.max(0, current.offset - current.limit) }))} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40">Previous</button>
                <button type="button" disabled={!pagination.hasMore} onClick={() => setFilters((current) => ({ ...current, offset: current.offset + current.limit }))} className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-40">Next</button>
              </div>
            </footer>
            </Panel>
          </div>
        </>
      )}

      {selectedJobId && <DetailModal jobId={selectedJobId} onClose={() => setSelectedJobId(null)} />}
    </div>
  );
}

export default JobInsightsPage;
